"use client";

import Image from "next/image"
import React, { useMemo } from "react"
import { colorMap } from "../colorMap"
import PlaceholderImage from "@modules/common/icons/placeholder-image"

type LayeredThumbnailProps = {
  images?: any[] | null
  size?: "small" | "medium" | "large" | "full" | "square"
  options?: Record<string, string | undefined>
  metadata?: Record<string, unknown>
}

const LayeredThumbnail: React.FC<LayeredThumbnailProps> = ({
  images,
  size = "small",
  options,
  metadata,
}) => {
  // Use metadata if provided (cart context), otherwise use options (product context)
  const effectiveOptions = useMemo(() => {
    if (metadata) {
      // Convert metadata to the expected format
      // The metadata from cart items is already in the correct format: { Base: 'White', Color 1: 'Orange', ... }
      // Make sure we're handling it as a Record<string, string>
      const result: Record<string, string | undefined> = {};

      // Convert all values to strings to ensure consistency
      Object.entries(metadata).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          result[key] = String(value);
        }
      });

      console.log("Using metadata for options:", result);
      return result;
    }
    console.log("Using provided options:", options || {});
    return options || {};
  }, [metadata, options]);

  // Process effective options

  // Process images to extract option information
  const imagesWithBaseNames = useMemo(() => {
    if (!images || images.length === 0) {
      return [];
    }

    return images.map((image) => {
      const baseName =
        decodeURIComponent(image.url)
          .replaceAll(/\d{13}\-/g, "")
          .split("/")
          .pop()
          ?.split(".")[0] || "";

      const [optionName, optionValue] = baseName.split("_");



      return {
        ...image,
        baseName,
        optionName,
        optionValue,
        // We'll handle the selectedOption separately for each image type
      };
    });
  }, [images]);

  // Filter base images
  const baseImages = useMemo(() => {
    const filtered = imagesWithBaseNames.filter(
      (image) => image.optionName === "Base"
    );

    return filtered;
  }, [imagesWithBaseNames]);

  // Get selected base images
  const selectedBaseImages = useMemo(() => {
    // If no options are selected, use the first base image
    if (Object.keys(effectiveOptions).length === 0 && baseImages.length > 0) {
      console.log("No options selected, using first base image");
      return [baseImages[0]];
    }

    // For base images, we need to check if the Base option in effectiveOptions matches the image's optionValue
    const baseOption = effectiveOptions["Base"];
    if (baseOption) {
      const filtered = baseImages.filter((image) => {
        const matches = baseOption === image.optionValue;
        return matches;
      });
      return filtered.length > 0 ? filtered : (baseImages.length > 0 ? [baseImages[0]] : []);
    }

    // If no base option is found, use the first base image
    return baseImages.length > 0 ? [baseImages[0]] : [];
  }, [baseImages, effectiveOptions]);

  // Filter color images
  const colorImages = useMemo(() => {
    const filtered = imagesWithBaseNames.filter((image) =>
      image.optionName.startsWith("Color")
    );

    return filtered;
  }, [imagesWithBaseNames]);



  // If no images or processing failed, return placeholder
  if (!images || images.length === 0 || selectedBaseImages.length === 0) {
    return (
      <div className="w-full h-full absolute inset-0 flex items-center justify-center">
        <PlaceholderImage size={size === "small" ? 16 : 24} />
      </div>
    );
  }

  return (
    <>
      {/* Base image */}
      {selectedBaseImages.map((image) => (
        <Image
          key={image.id}
          src={image.url}
          alt="Thumbnail"
          className="absolute inset-0 object-contain object-center max-h-[400px] mx-auto"
          draggable={false}
          quality={50}
          sizes="(max-width: 576px) 280px, (max-width: 768px) 360px, (max-width: 992px) 480px, 800px"
          fill
          unoptimized
        />
      ))}

      {/* Color overlays */}
      {colorImages.map((image) => {
        // Get the selected option for this color
        // For example, if image.optionName is "Color 1", get effectiveOptions["Color 1"]
        const selectedOption = effectiveOptions[image.optionName];
        const colorFromMap = selectedOption ? (colorMap[selectedOption] || selectedOption) : undefined;

        if (!selectedOption) {
          return null;
        }

        return (
          <div
            key={image.id}
            className="absolute overflow-hidden inset-0 max-h-[400px] mx-auto"
            style={{
              backgroundColor: colorFromMap,
              objectFit: "contain",
              maskRepeat: "no-repeat",
              maskPosition: "center",
              maskSize: "contain",
              mixBlendMode: "multiply",
              maskImage: `url('${image.url.replace(" ", "%20")}')`,
            }}
          />
        );
      })}
    </>
  );
};

export default LayeredThumbnail;
